import { type MouseEvent, useLayoutEffect, useRef, useState } from "react";
import { eventNames } from "@/modules/Constants";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import type { ExperienceState } from "@/types/state";
import {
  ANNOTATION,
  ANNOTATION_TEXT,
  ANNOTATION_WRAPPER,
  CONTROLS,
  CTA,
  FOOTER,
} from "@/components/shopwindow/productFooterClasses";

const REGION = "Shop Window Cta Footer";

interface CtaLink {
  label?: { click?: { title?: string } };
}

type ShopWindowCtaFooterProps = {
  experienceState: ExperienceState;
  config: ShopWindowConfig;
  onApplicationEvent?: (event: Record<string, unknown>) => void;
};

export default function ShopWindowCtaFooter({
  experienceState,
  config,
  onApplicationEvent,
}: ShopWindowCtaFooterProps) {
  // Button key counter; bumped during render, as the class did.
  const [keys] = useState(() => ({ ctaId: 1 }));
  const cta = useRef<unknown>(undefined);
  const didMount = useRef(false);

  // componentDidUpdate: runs on every commit except the first.
  useLayoutEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    const clusterIndex = config.clusterArticleIndexAt(
      experienceState.experienceData,
      config.index,
    );

    if (clusterIndex.cta !== cta.current) {
      if (onApplicationEvent) {
        onApplicationEvent({
          region: REGION,
          name: eventNames.CTA_DATA_DID_UPDATE,
          cta: clusterIndex.cta,
        });
      }

      cta.current = clusterIndex.cta;
    }
  });

  const onCtaButtonClick = (e: MouseEvent) => {
    e.preventDefault();

    const clusterIndex = config.clusterArticleIndexAt(
      experienceState.experienceData,
      config.index,
    );

    if (onApplicationEvent) {
      if (clusterIndex && clusterIndex.cta) {
        onApplicationEvent({
          region: REGION,
          name: eventNames.LINK,
          clusterName: clusterIndex.clusterName,
          cindex: clusterIndex.cindex,
          index: clusterIndex.index,
          link: clusterIndex.cta,
        });
      }
    }
  };

  const experienceData = experienceState.experienceData;
  const applicationData = experienceState.applicationData;
  const clusterIndex = config.clusterArticleIndexAt(
    experienceData,
    config.index,
  );

  // Hidden and active follow the config, not whether an article exists.
  const hasLink = !!clusterIndex.cta;
  const isHidden = clusterIndex.clusterName !== config.clusterName;
  const visibility = isHidden ? "hidden" : "visible";
  const pagination = config.data.showPagination ? (
    <div className="swiper-pagination" />
  ) : null;

  if (cta.current !== clusterIndex.cta) {
    keys.ctaId++;
  }

  let controls = null;

  if (hasLink && applicationData.isLandscape()) {
    const isSelected = false;
    const isActive = hasLink && !isSelected;
    const favoriteClass = isSelected ? " selected" : "";
    const hoverClass = !isSelected && isActive ? " hover" : "";
    const activeClass = isActive ? " active" : " inactive";
    const label = clusterIndex.cta ? (clusterIndex.cta as CtaLink).label : null;
    const title = label && label.click ? label.click.title : null;

    controls = (
      <div>
        <div className={`shop-window-footer-controls ${CONTROLS}`}>
          <button
            key={`cta-btn-${keys.ctaId}`}
            type="button"
            className={`cta-btn button type-1 text wide ${CTA}${activeClass}${favoriteClass}${hoverClass}`}
            onClick={onCtaButtonClick}
          >
            <span>{title}</span>
          </button>
        </div>
      </div>
    );
  }

  const annotation = experienceData.getArticleAnnotation(
    clusterIndex.clusterName,
    clusterIndex.cindex,
    clusterIndex.index,
  );

  return (
    <div
      className={`shop-window-footer tw:w-full ${FOOTER}`}
      style={{ visibility: visibility }}
    >
      <div className={`panel-annotation-wrapper ${ANNOTATION_WRAPPER}`}>
        {pagination}
        <div className={`annotation ${ANNOTATION}`}>
          <span className={`annotation-text ${ANNOTATION_TEXT}`}>
            {annotation}
          </span>
        </div>
      </div>
      {controls}
    </div>
  );
}
